import { MutationCache, QueryClient } from '@tanstack/react-query';
import { dashboardKeys } from '@/features/dashboard/api';
import { ApiError } from './api';

/** Key of `GET /notifications/counts`, the one query behind every rail badge. */
export const NOTIFICATION_COUNTS_KEY = ['notifications', 'counts'] as const;

export const queryClient = new QueryClient({
  // Any successful write can move a rail badge (closing an appointment drops
  // needsClosure, resolving a lab order drops its queue…). Refreshing the counts
  // here keeps every badge honest without each feature's mutation having to
  // know which badges it touches. The dashboard is the same story: every KPI on
  // it is downstream of some write, and with a 60s staleTime it would otherwise
  // show pre-write numbers when the user navigates back to it.
  mutationCache: new MutationCache({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_COUNTS_KEY });
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  }),
  defaultOptions: {
    queries: {
      // Clinic screens stay open all day; a short stale window keeps the
      // calendar and the doctor queue honest without hammering the API.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Never retry a permission or validation failure — it will not change.
        if (error instanceof ApiError && error.statusCode >= 400 && error.statusCode < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});
